import { Plus, Trash2 } from 'lucide-react';
import type { StockItem } from '@/types/oscar';
import { blankPart } from './componentDrafts';
import type { PartDraft } from './componentDrafts';

const cellCls = 'w-full rounded border border-border bg-white px-2 py-1 text-sm font-normal';

/**
 * The parts a stock line is made of (106).
 *
 * <p>Anthony, 13/10/2026: <i>"for some stock items are actually multiple small
 * items that we buy. I dont want to stock them individually. but we need to
 * capture it in the description for when we request for quote."</i></p>
 *
 * ⛔ SITS UNDER THE DESCRIPTION, which is where Anthony asked for it and also
 * where it reads correctly: the description says what the thing IS, and the
 * parts itemise it. A quote request is rendered the same way round.
 *
 * ⛔ A PART CAN POINT AT A STOCK LINE. Some parts are stocked in their own
 * right — a USB-C cable is already a line. Pointing at it means the name and
 * the part number come from that line LIVE, so correcting it once corrects
 * every kit that contains one. The typed boxes are then disabled rather than
 * hidden, so it is visible that the link is supplying them and nothing is
 * being silently overridden.
 */
export function ComponentsEditor({ parts, onChange, selfStockItemId, stockLines, disabled }: {
  parts: PartDraft[];
  onChange: (next: PartDraft[]) => void;
  /**
   * ⚠ null WHILE ADDING, because the line does not exist yet — and a line that
   * does not exist cannot be a part of itself, so there is nothing to exclude.
   */
  selfStockItemId: number | null;
  stockLines: StockItem[];
  disabled?: boolean;
}) {
  const set = (key: string, patch: Partial<PartDraft>) =>
    onChange(parts.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  // ⚠ A kit cannot contain itself (CK_StockItemComponent_NotSelf), and
  //   offering it would be offering a choice the server refuses.
  const linkable = stockLines
    .filter((s) => s.StockItemId !== selfStockItemId)
    .sort((a, b) => a.Name.localeCompare(b.Name));

  return (
    <fieldset className="mt-2 rounded border border-border bg-white px-2 py-2">
      <legend className="px-1 text-xs font-semibold text-darkGrey">Made up of</legend>
      <p className="text-xs font-normal text-darkGrey/70">
        For a line that is really several things bought together. These are not stocked
        separately — the count stays on this line. They are listed under the description when
        we ask a supplier to quote it.
      </p>

      {parts.length === 0 && (
        <p className="mt-2 text-xs font-normal text-darkGrey/60">
          Nothing listed — this is a single item.
        </p>
      )}

      {parts.length > 0 && (
        <div className="mt-2 space-y-2">
          {parts.map((p, i) => {
            const linked = p.ComponentStockItemId !== null;
            return (
              <div key={p.key} className="rounded border border-border bg-bg/40 px-2 py-2">
                <div className="flex items-start gap-2">
                  <label className="w-16 shrink-0 text-xs font-semibold text-darkGrey">
                    Qty
                    <input
                      type="number"
                      min={1}
                      aria-label={`Quantity for part ${i + 1}`}
                      value={p.Qty}
                      disabled={disabled}
                      // ⛔ AN EMPTY BOX STAYS EMPTY while the person types.
                      //   Coercing to 1 here is what made clearing the box and
                      //   typing "2" produce 12 — the 1 came back before the
                      //   keystroke landed. The coercion belongs in toWrite.
                      onChange={(e) => {
                        const v = e.target.value;
                        set(p.key, { Qty: v === '' ? '' : (Number(v) >= 1 ? Number(v) : 1) });
                      }}
                      className={`mt-1 ${cellCls}`}
                    />
                  </label>

                  <div className="min-w-0 flex-1">
                    <label className="block text-xs font-semibold text-darkGrey">
                      Part
                      <input
                        aria-label={`Description for part ${i + 1}`}
                        value={linked ? (p.LinkedName ?? '') : p.Description}
                        disabled={disabled || linked}
                        placeholder="Rally Mic Pod"
                        onChange={(e) => set(p.key, { Description: e.target.value })}
                        className={`mt-1 ${cellCls} ${linked ? 'bg-bg text-darkGrey/70' : ''}`}
                      />
                    </label>
                  </div>

                  <div className="w-40 shrink-0">
                    <label className="block text-xs font-semibold text-darkGrey">
                      Part number
                      <input
                        aria-label={`Part number for part ${i + 1}`}
                        value={linked ? (p.LinkedPartNumber ?? '') : p.SupplierPartNumber}
                        disabled={disabled || linked}
                        placeholder="MPN or SKU"
                        onChange={(e) => set(p.key, { SupplierPartNumber: e.target.value })}
                        className={`mt-1 ${cellCls} ${linked ? 'bg-bg text-darkGrey/70' : ''}`}
                      />
                    </label>
                  </div>

                  <button
                    type="button"
                    aria-label={`Remove part ${i + 1}`}
                    disabled={disabled}
                    onClick={() => onChange(parts.filter((x) => x.key !== p.key))}
                    className="mt-5 shrink-0 rounded border border-border px-2 py-1 text-red hover:bg-red/10 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>

                <label className="mt-1 block text-xs font-semibold text-darkGrey">
                  Is this a stock line we hold?
                  <select
                    aria-label={`Stock line for part ${i + 1}`}
                    value={p.ComponentStockItemId ?? ''}
                    disabled={disabled}
                    onChange={(e) => set(p.key, {
                      ComponentStockItemId: e.target.value === '' ? null : Number(e.target.value),
                      // ⚠ The linked name is only known for a line in this
                      //   list; clearing the link clears what it was showing
                      //   so a stale name cannot linger in a disabled box.
                      LinkedName: e.target.value === ''
                        ? null
                        : (linkable.find((s) => s.StockItemId === Number(e.target.value))?.Name ?? null),
                      LinkedPartNumber: e.target.value === ''
                        ? null
                        : (linkable.find((s) => s.StockItemId === Number(e.target.value))?.SupplierPartNumber ?? null),
                      LinkedIsRetired: false,
                    })}
                    className={`mt-1 ${cellCls}`}
                  >
                    <option value="">No — just describe it</option>
                    {linkable.map((s) => (
                      <option key={s.StockItemId} value={s.StockItemId}>{s.Name}</option>
                    ))}
                  </select>
                  <span className="mt-0.5 block font-normal text-darkGrey/70">
                    {linked
                      ? 'Name and part number come from that line, so correcting it there corrects this.'
                      : 'Leave as is for a part we do not stock on its own.'}
                  </span>
                </label>

                {p.LinkedIsRetired && (
                  <p role="alert" className="mt-1 rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs font-normal text-darkGrey">
                    That stock line has been retired. The part still has a name, but check it can
                    still be ordered before sending a quote request.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange([...parts, blankPart()])}
        className="mt-2 inline-flex items-center gap-1 rounded border border-navy px-2 py-1 text-xs font-semibold text-navy hover:bg-navy/5 disabled:opacity-50"
      >
        <Plus className="h-3 w-3" aria-hidden /> Add a part
      </button>
    </fieldset>
  );
}
